import React, { useState, useRef, useEffect } from 'react';
import {
  Card,
  Form,
  Button,
  Badge,
  Spinner,
  Alert,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import {
  FaArrowLeft,
  FaPaperPlane,
  FaPaperclip,
  FaSmile,
  FaUser,
  FaShieldAlt,
  FaCheck,
  FaClock,
  FaGift,
  FaImage,
  FaHeart,
  FaLightbulb,
  FaReply,
  FaTrash,
  FaEllipsisV,
} from 'react-icons/fa';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import EmojiPicker from 'emoji-picker-react';
import { useTheme } from '../../contexts/ThemeContext';
import { adminDiscussionAPI } from '../../services/api';
import giftService from '../../services/giftService';
import walletService from '../../services/walletService';
import './AdminDiscussions.css';

const DiscussionDetail = ({ discussion, onSendMessage, onBack }) => {
  const { theme, isDark } = useTheme();
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGiftPicker, setShowGiftPicker] = useState(false);
  const [showIcebreakers, setShowIcebreakers] = useState(false);
  const [gifts, setGifts] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [replyingTo, setReplyingTo] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showMessageMenu, setShowMessageMenu] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const emojiPickerRef = useRef(null);

  // Get participants from discussion
  const participants = React.useMemo(() => {
    if (!discussion) return [];
    const list = [];
    if (discussion.participant1) list.push(discussion.participant1);
    if (discussion.participant2) list.push(discussion.participant2);
    return list;
  }, [discussion]);

  // Auto-select first participant if none selected
  useEffect(() => {
    if (participants.length > 0 && !selectedParticipant) {
      setSelectedParticipant(participants[0]);
    }
  }, [participants, selectedParticipant]);

  // Fetch messages, gifts and wallet on mount
  useEffect(() => {
    if (discussion?.id) {
      fetchMessages();
      fetchGifts();
      fetchWalletBalance();
    }
  }, [discussion?.id]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await adminDiscussionAPI.getMessages(discussion.id);
      const messagesData = response.data?.data?.messages;
      if (Array.isArray(messagesData)) {
        setMessages(messagesData);
      } else if (messagesData?.data && Array.isArray(messagesData.data)) {
        setMessages(messagesData.data);
      } else {
        setMessages([]);
      }
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des messages');
      console.error('Error fetching messages:', err);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGifts = async () => {
    try {
      const response = await giftService.getAvailableGifts();
      console.log('Gifts API response:', response.data);
      
      // Handle different response structures
      let giftsData = [];
      
      if (response.data?.success && response.data.data) {
        // API returns object with keys: small, medium, large, premium
        const giftsObj = response.data.data;
        giftsData = Object.entries(giftsObj).map(([key, value]) => ({
          id: key,
          type: key,
          name: value.name,
          cost: value.cost,
          price: value.cost, // alias for compatibility
          icon: value.icon,
        }));
      }
      
      console.log('Processed gifts:', giftsData);
      setGifts(giftsData);
    } catch (error) {
      console.error('Error fetching gifts:', error);
      setGifts([]); // Ensure gifts is always an array
    }
  };




  const fetchWalletBalance = async () => {
    try {
      const response = await walletService.getWallet();
      if (response.data?.success) {
        setWalletBalance(response.data.data?.balance || 0);
      }
    } catch (error) {
      console.error('Error fetching wallet balance:', error);
    }
  };

  const handleSend = async () => {
    if (!message.trim() || !selectedParticipant) return;

    setSending(true);
    try {
      const messageData = {
        message: message.trim(),
        as_user_id: selectedParticipant.id,
        reply_to_id: replyingTo?.id || null,
      };
      
      await adminDiscussionAPI.sendMessage(discussion.id, messageData);
      setMessage('');
      setReplyingTo(null);
      setShowEmojiPicker(false);
      await fetchMessages();
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleEmojiClick = (emojiData) => {
    setMessage((prev) => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !discussion?.id || !selectedParticipant) return;

    const formData = new FormData();
    formData.append('media', file);
    formData.append('as_user_id', selectedParticipant.id);

    try {
      setSending(true);
      await adminDiscussionAPI.uploadMedia(discussion.id, formData);
      await fetchMessages();
    } catch (error) {
      console.error('Failed to upload image:', error);
      setError('Erreur lors de l\'envoi de l\'image');
    } finally {
      setSending(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleGiftSend = async (gift) => {
    if (!discussion?.id || !selectedParticipant || !gift) return;

    const otherParticipant = participants.find(p => p.id !== selectedParticipant.id);
    if (!otherParticipant) {
      setError('Impossible de trouver le destinataire');
      return;
    }

    try {
      setSending(true);
      await giftService.sendGift(
        otherParticipant.id,
        gift.type,
        `🎁 Cadeau de ${selectedParticipant.username}`,
        false,
        selectedParticipant.id
      );


      
      // Send a message about the gift
      await adminDiscussionAPI.sendMessage(discussion.id, {
        message: `🎁 a envoyé un cadeau : ${gift.name}`,
        as_user_id: selectedParticipant.id,
        message_type: 'gift',
      });
      
      await fetchMessages();
      await fetchWalletBalance();
      setShowGiftPicker(false);
    } catch (error) {
      console.error('Error sending gift:', error);
      setError(error.response?.data?.message || 'Erreur lors de l\'envoi du cadeau');
    } finally {
      setSending(false);
    }
  };

  const handleReply = (msg) => {
    setReplyingTo(msg);
    setShowMessageMenu(null);
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await adminDiscussionAPI.deleteMessage(discussion.id, messageId);
      await fetchMessages();
      setShowMessageMenu(null);
    } catch (error) {
      console.error('Error deleting message:', error);
      setError('Erreur lors de la suppression du message');
    }
  };

  const getParticipant = (userId) => {
    if (!discussion) return null;
    if (discussion.participant1?.id === userId) return discussion.participant1;
    if (discussion.participant2?.id === userId) return discussion.participant2;
    return null;
  };

  // Icebreaker suggestions
  const icebreakers = [
    "Comment s'est passée ta journée ?",
    "As-tu des projets pour ce week-end ?",
    "Quel est ton film préféré ?",
    "Tu préfères la mer ou la montagne ?",
    "Quel est ton hobby préféré ?",
  ];

  if (!discussion) {
    return (
      <div className={`discussion-empty-state ${isDark ? 'dark' : ''}`}>
        <FaArrowLeft size={48} className="mb-3" />
        <p>Sélectionnez une discussion pour voir les détails</p>
      </div>
    );
  }

  return (
    <div className={`discussion-detail-container ${isDark ? 'dark' : ''}`}>
      {/* Header */}
      <Card.Header className={`discussion-detail-header ${isDark ? 'bg-dark text-light' : 'bg-white'}`}>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-3">
            <Button variant={isDark ? "outline-light" : "outline-secondary"} size="sm" onClick={onBack}>
              <FaArrowLeft />
            </Button>
            <div>
              <h6 className="mb-0">
                Discussion #{discussion.id?.substring(0, 8)}
              </h6>
              <small className={isDark ? "text-light" : "text-muted"}>
                {discussion.participant1?.username || 'Utilisateur 1'} & {discussion.participant2?.username || 'Utilisateur 2'}
              </small>
            </div>
          </div>
          
          {/* Participant Selector */}
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className={`small ${isDark ? 'text-light' : 'text-muted'}`}>Répondre en tant que:</span>
            {participants.map((participant) => (
              <OverlayTrigger
                key={participant.id}
                placement="top"
                overlay={<Tooltip>{participant.first_name && participant.last_name 
                  ? `${participant.first_name} ${participant.last_name}`
                  : participant.username}</Tooltip>}
              >
                <Button
                  variant={selectedParticipant?.id === participant.id 
                    ? 'primary' 
                    : isDark ? 'outline-light' : 'outline-secondary'}
                  size="sm"
                  onClick={() => setSelectedParticipant(participant)}
                  className="d-flex align-items-center gap-2"
                >
                  {participant.avatar ? (
                    <img
                      src={participant.avatar}
                      alt={participant.username}
                      className="rounded-circle"
                      style={{ width: 20, height: 20, objectFit: 'cover' }}
                    />
                  ) : (
                    <FaUser size={12} />
                  )}
                  {participant.first_name || participant.username}
                  {selectedParticipant?.id === participant.id && <FaCheck size={10} />}
                </Button>
              </OverlayTrigger>
            ))}
          </div>
        </div>
      </Card.Header>

      {/* Messages */}
      <Card.Body className={`discussion-messages-area ${isDark ? 'bg-dark' : ''}`}>
        {loading ? (
          <div className="discussion-loading-state">
            <Spinner animation="border" variant={isDark ? "light" : "primary"} />
            <p className={`mt-3 ${isDark ? 'text-light' : 'text-muted'}`}>Chargement des messages...</p>
          </div>
        ) : error ? (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>
        ) : messages.length === 0 ? (
          <div className={`discussion-empty-state ${isDark ? 'dark' : ''}`}>
            <FaSmile size={48} className="mb-3" />
            <p>Aucun message dans cette discussion</p>
            <small className={isDark ? 'text-light' : 'text-muted'}>Soyez le premier à envoyer un message !</small>
          </div>
        ) : (
          <div className="messages-list">
            {messages.map((msg, index) => {
              const sender = getParticipant(msg.sender_id);
              const isAdminMessage = msg.sent_by_admin;
              const isOwn = msg.sender_id === selectedParticipant?.id;
              const showAvatar = index === 0 || messages[index - 1]?.sender_id !== msg.sender_id;

              return (
                <div
                  key={msg.id}
                  className={`message-wrapper ${isOwn ? 'own' : 'other'}`}
                >
                  <div className={`message-bubble ${isOwn ? 'own' : 'other'} ${isAdminMessage ? 'admin' : ''} ${isDark ? 'dark' : ''}`}>
                    {showAvatar && !isOwn && (
                      <div className="message-avatar">
                        {sender?.avatar ? (
                          <img src={sender.avatar} alt={sender.username} />
                        ) : (
                          <FaUser />
                        )}
                      </div>
                    )}
                    
                    <div className="message-content">
                      {showAvatar && (
                        <small className={`sender-name ${isDark ? 'text-light' : 'text-muted'}`}>
                          {sender?.first_name && sender?.last_name 
                            ? `${sender.first_name} ${sender.last_name}`
                            : sender?.username || 'Utilisateur'}
                          {isAdminMessage && (
                            <Badge bg="warning" className="ms-1" pill>
                              <FaShieldAlt size={10} /> Admin
                            </Badge>
                          )}
                        </small>
                      )}
                      
                      {msg.reply_to && (
                        <div className={`reply-reference ${isDark ? 'dark' : ''}`}>
                          <small>Réponse à: {msg.reply_to.text?.substring(0, 50)}...</small>
                        </div>
                      )}
                      
                      {msg.message_type === 'image' || msg.media_file ? (
                        <img
                          src={msg.media_file || msg.media_url}
                          alt="Media"
                          className="message-image"
                          onClick={() => setSelectedImage(msg.media_file || msg.media_url)}
                        />
                      ) : msg.message_type === 'gift' ? (
                        <div className="gift-message">
                          <FaGift size={32} className="text-danger mb-2" />
                          <p>{msg.text}</p>
                        </div>
                      ) : (
                        <p className="message-text">{msg.text}</p>
                      )}
                      
                      <div className={`message-meta ${isDark ? 'text-light' : 'text-muted'}`}>
                        <small>
                          <FaClock size={10} className="me-1" />
                          {formatDistanceToNow(new Date(msg.created_at), {
                            addSuffix: true,
                            locale: fr,
                          })}
                        </small>
                        {isOwn && msg.is_read && (
                          <FaCheck size={10} className="ms-1 text-primary" />
                        )}
                      </div>
                    </div>
                    
                    {/* Message Menu */}
                    <Button
                      variant="link"
                      className={`message-menu-btn ${isDark ? 'text-light' : 'text-muted'}`}
                      onClick={() => setShowMessageMenu(showMessageMenu === msg.id ? null : msg.id)}
                    >
                      <FaEllipsisV size={12} />
                    </Button>
                    
                    {showMessageMenu === msg.id && (
                      <div className={`message-menu ${isDark ? 'dark' : ''}`}>
                        <Button variant="link" size="sm" onClick={() => handleReply(msg)}>
                          <FaReply size={12} /> Répondre
                        </Button>
                        {isOwn && (
                          <Button variant="link" size="sm" className="text-danger" onClick={() => handleDeleteMessage(msg.id)}>
                            <FaTrash size={12} /> Supprimer
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </Card.Body>

      {/* Input Area */}
      <Card.Footer className={`discussion-input-area ${isDark ? 'bg-dark border-secondary' : 'bg-white'}`}>
        {/* Reply Preview */}
        {replyingTo && (
          <div className={`reply-preview ${isDark ? 'dark' : ''}`}>
            <small>Réponse à: {replyingTo.text?.substring(0, 50)}...</small>
            <Button variant="link" size="sm" onClick={() => setReplyingTo(null)}>×</Button>
          </div>
        )}

        {/* Icebreakers */}
        {showIcebreakers && (
          <div className={`icebreakers-panel ${isDark ? 'dark' : ''}`}>
            <small className="mb-2 d-block">💡 Suggestions pour briser la glace:</small>
            <div className="d-flex gap-2 flex-wrap">
              {icebreakers.map((text, idx) => (
                <Button
                  key={idx}
                  variant={isDark ? "outline-light" : "outline-primary"}
                  size="sm"
                  onClick={() => {
                    setMessage(text);
                    setShowIcebreakers(false);
                  }}
                >
                  {text}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Gift Picker */}
        {showGiftPicker && (
          <div className={`gift-picker-panel ${isDark ? 'dark' : ''}`}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small>🎁 Choisir un cadeau (Solde: {walletBalance} diamants):</small>
              <Button variant="link" size="sm" onClick={() => setShowGiftPicker(false)}>×</Button>
            </div>
            {gifts.length === 0 ? (
              <Alert variant="info" className="mb-0">
                Aucun cadeau disponible pour le moment.
              </Alert>
            ) : (
              <div className="gifts-grid">
                {gifts.map((gift) => (
                  <OverlayTrigger
                    key={gift.id}
                    placement="top"
                    overlay={<Tooltip>{gift.name} - {gift.cost} diamants</Tooltip>}
                  >
                    <Button
                      variant={isDark ? "dark" : "light"}
                      className="gift-item"
                      onClick={() => handleGiftSend(gift)}
                      disabled={walletBalance < gift.cost}
                    >
                      <span style={{ fontSize: '32px' }}>{gift.icon}</span>
                      <small>{gift.cost} 💎</small>
                    </Button>
                  </OverlayTrigger>
                ))}
              </div>
            )}
          </div>
        )}


        {/* Emoji Picker */}
        {showEmojiPicker && (
          <div className="emoji-picker-container" ref={emojiPickerRef}>
            <EmojiPicker 
              onEmojiClick={handleEmojiClick} 
              theme={isDark ? 'dark' : 'light'}
              emojiStyle="native"
              height={350}
              width={350}
              searchPlaceholder="Rechercher un emoji..."
              skinTonesDisabled
            />
          </div>
        )}


        {!selectedParticipant ? (
          <Alert variant="warning" className="mb-0">
            <FaShieldAlt className="me-2" />
            Veuillez sélectionner un participant pour envoyer des messages
          </Alert>
        ) : (
          <div className="input-actions-row">
            <div className="action-buttons">
              <div style={{ position: 'relative' }}>
                <OverlayTrigger placement="top" overlay={<Tooltip>Émojis</Tooltip>}>
                  <Button
                    variant={isDark ? "outline-light" : "outline-secondary"}
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    active={showEmojiPicker}
                  >
                    <FaSmile />
                  </Button>
                </OverlayTrigger>
                
                {/* Emoji Picker - positioned relative to this container */}
                {showEmojiPicker && (
                  <div className="emoji-picker-container" ref={emojiPickerRef}>
                    <EmojiPicker 
                      onEmojiClick={handleEmojiClick} 
                      theme={isDark ? 'dark' : 'light'}
                      emojiStyle="native"
                      height={350}
                      width={350}
                      searchPlaceholder="Rechercher un emoji..."
                      skinTonesDisabled
                    />
                  </div>
                )}
              </div>

              
              <OverlayTrigger placement="top" overlay={<Tooltip>Image</Tooltip>}>
                <Button
                  variant={isDark ? "outline-light" : "outline-secondary"}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FaImage />
                </Button>
              </OverlayTrigger>
              
              <OverlayTrigger placement="top" overlay={<Tooltip>Cadeau</Tooltip>}>
                <Button
                  variant={isDark ? "outline-light" : "outline-secondary"}
                  onClick={() => setShowGiftPicker(!showGiftPicker)}
                  active={showGiftPicker}
                >
                  <FaGift />
                </Button>
              </OverlayTrigger>
              
              <OverlayTrigger placement="top" overlay={<Tooltip>Cassez la glace</Tooltip>}>
                <Button
                  variant={isDark ? "outline-light" : "outline-secondary"}
                  onClick={() => setShowIcebreakers(!showIcebreakers)}
                  active={showIcebreakers}
                >
                  <FaLightbulb />
                </Button>
              </OverlayTrigger>
              
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                style={{ display: 'none' }}
              />
            </div>

            <div className="message-input-wrapper">
              <Form.Control
                as="textarea"
                rows={1}
                placeholder={`Écrivez votre message en tant que ${selectedParticipant.first_name || selectedParticipant.username}...`}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                className={`message-input ${isDark ? 'bg-dark text-light border-secondary' : ''}`}
                style={{ resize: 'none' }}
              />
            </div>

            <Button
              variant="primary"
              onClick={handleSend}
              disabled={!message.trim() || sending}
              className="send-btn"
            >
              {sending ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <FaPaperPlane />
              )}
            </Button>
          </div>
        )}
      </Card.Footer>

      {/* Image Modal */}
      {selectedImage && (
        <div className="image-modal" onClick={() => setSelectedImage(null)}>
          <img src={selectedImage} alt="Full size" />
        </div>
      )}
    </div>
  );
};

export default DiscussionDetail;
